<template>
  <div
    v-if="open"
    class="fixed z-[1260] rounded-2xl border border-[#ff009d49] bg-white/95 shadow-2xl p-4 sm:p-5 overflow-y-auto app-scroll"
    :style="menuStyle"
    @click.stop
  >
    <div class="flex items-center justify-between mb-4">
      <h5 class="text-sm font-semibold text-zinc-700">
        Image Settings
      </h5>
      <button
        type="button"
        class="rounded-md p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100"
        @click="$emit('close')"
      >
        <XMarkIcon class="w-4 h-4" />
      </button>
    </div>

    <div class="space-y-5">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Preview
        </p>
        <div class="rounded-lg overflow-hidden bg-zinc-100">
          <img
            :src="previewUrl"
            alt="Image preview"
            class="w-full h-auto object-cover"
          >
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Image Source
        </p>
        <div>
          <button
            type="button"
            class="w-full rounded-md bg-[#2D8CFF] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1f77e6]"
            @click="$emit('media-request')"
          >
            Select From Media Content
          </button>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Link Address
        </p>
        <input
          type="text"
          class="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
          :value="normalizedImageLinkAddress"
          placeholder="https://example.com"
          @input="onImageLinkAddressInput"
        >
      </div>

      <label class="flex items-center justify-between gap-3">
        <span class="text-sm text-zinc-700 font-medium">Open In New Tab</span>
        <input
          type="checkbox"
          class="h-4 w-4 rounded border-zinc-300 text-[#ff009dce] focus:ring-[#ff009dce]"
          :checked="normalizedImageOpenInNewTab"
          @change="onImageOpenInNewTabChange"
        >
      </label>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Width
        </p>
        <div class="flex items-center gap-3">
          <input
            type="range"
            min="20"
            max="100"
            step="1"
            class="w-full accent-[#ff009dce]"
            :value="normalizedImageWidth"
            @input="onWidthRangeInput"
          >
          <div class="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="20"
              max="100"
              step="1"
              class="w-16 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-[#ff009d85] focus:border-[#ff009d85]"
              :value="normalizedImageWidth"
              @input="onWidthNumberInput"
            >
            <span class="text-xs text-zinc-500">%</span>
          </div>
        </div>
      </div>

      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">
          Alignment
        </p>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="option in alignmentOptions"
            :key="option.value"
            type="button"
            class="rounded-md border px-2 py-1.5 text-sm transition-colors"
            :class="normalizedImageAlign === option.value
              ? 'border-[#ff009dce] bg-[#ff009d14] text-[#ff009dce]'
              : 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'"
            @click="updateSettings({ imageAlign: option.value })"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  menuStyle: {
    type: Object,
    default: () => ({ top: '0px', left: '0px', width: '320px', height: '360px' }),
  },
  settings: {
    type: Object,
    default: () => ({
      imageWidth: 100,
      imageAlign: 'center',
      imageLinkAddress: '',
      imageOpenInNewTab: false,
    }),
  },
  previewUrl: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:settings', 'close', 'media-request']);

const clampImageWidth = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 100;
  return Math.min(100, Math.max(20, Math.round(num)));
};

const normalizeAlign = (value) => {
  const v = String(value || '').toLowerCase();
  if (v === 'left' || v === 'center' || v === 'right') return v;
  return 'center';
};

const normalizedImageWidth = computed(() => clampImageWidth(props.settings?.imageWidth));
const normalizedImageAlign = computed(() => normalizeAlign(props.settings?.imageAlign));
const normalizedImageLinkAddress = computed(() =>
  String(props.settings?.imageLinkAddress ?? props.settings?.image_link_address ?? '').trim()
);
const normalizedImageOpenInNewTab = computed(() =>
  (props.settings?.imageOpenInNewTab ?? props.settings?.image_open_in_new_tab) === true
);

const alignmentOptions = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const updateSettings = (patch = {}) => {
  emit('update:settings', {
    imageWidth: clampImageWidth(patch.imageWidth ?? normalizedImageWidth.value),
    imageAlign: normalizeAlign(patch.imageAlign ?? normalizedImageAlign.value),
    imageLinkAddress: String(
      patch.imageLinkAddress ??
      patch.image_link_address ??
      normalizedImageLinkAddress.value
    ).trim(),
    imageOpenInNewTab: (patch.imageOpenInNewTab ?? patch.image_open_in_new_tab ?? normalizedImageOpenInNewTab.value) === true,
  });
};

const onWidthRangeInput = (event) => {
  updateSettings({ imageWidth: event?.target?.value });
};

const onWidthNumberInput = (event) => {
  updateSettings({ imageWidth: event?.target?.value });
};

const onImageLinkAddressInput = (event) => {
  updateSettings({ imageLinkAddress: event?.target?.value });
};

const onImageOpenInNewTabChange = (event) => {
  updateSettings({ imageOpenInNewTab: event?.target?.checked === true });
};
</script>

<style scoped>
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  scrollbar-gutter: stable;
}

.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent;
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>
